var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import classnames from 'classnames';
import React, { useState, useCallback, useMemo, useRef } from 'react';
import { isEmpty } from '../_utils/function';
import { useMount, useUpdate } from '../_utils/hooks';
import { isIOS } from '../_utils/platform';
import { createPrefixCls, substring } from '../_utils/string';
import { Icon } from '../icon';
import { convertValue } from '../input/utils';
const prefixCls = createPrefixCls('textarea');
export const Textarea = (_a) => {
    var { style, className, wrapClassName, defaultValue, value, rows = 3, autoHeight = false, formValue, maxLength, autoComplete = 'off', pagination = true, allowClear, onChange, onFocus, onBlur, onClear, onOverMaxLength, setFormValue } = _a, restProps = __rest(_a, ["style", "className", "wrapClassName", "defaultValue", "value", "rows", "autoHeight", "formValue", "maxLength", "autoComplete", "pagination", "allowClear", "onChange", "onFocus", "onBlur", "onClear", "onOverMaxLength", "setFormValue"]);
    const defaultValueRef = useRef(defaultValue);
    const getPropsValue = useMemo(() => {
        if (!isEmpty(value)) {
            return value;
        }
        else if (!isEmpty(formValue)) {
            return formValue;
        }
        else {
            return undefined;
        }
    }, [formValue, value]);
    const [textareaValue, setTextareaValue] = useState(getPropsValue === undefined
        ? isEmpty(defaultValueRef.current)
            ? ''
            : defaultValueRef.current
        : getPropsValue);
    const [height, setHeight] = useState();
    const $textarea = useRef();
    const isOnComposition = useRef(false);
    const isOnInputExcuted = useRef(false);
    const id = useRef(String(Math.random()));
    useMount(() => {
        if (setFormValue) {
            if (!isEmpty(value)) {
                setFormValue(value, false);
            }
            else if (!isEmpty(defaultValueRef.current)) {
                setFormValue(defaultValueRef.current, false);
            }
        }
    });
    // value更新
    useUpdate(() => {
        setTextareaValue(convertValue(value));
        setFormValue && setFormValue(convertValue(value), false);
    }, [value]);
    // formValue更新，一般用来做重置操作，值为undefined
    useUpdate(() => {
        setTextareaValue(convertValue(formValue));
    }, [formValue]);
    const handleChange = useCallback((e) => {
        e.persist && e.persist();
        const target = e.target;
        let value = target.value;
        setTextareaValue(convertValue(value));
        if (!isOnComposition.current && !isOnInputExcuted.current) {
            if (maxLength && value.length > maxLength) {
                value = substring(value, maxLength);
                onOverMaxLength && onOverMaxLength();
                setTextareaValue(convertValue(value));
            }
            if (autoHeight && $textarea.current) {
                setHeight($textarea.current.scrollHeight);
            }
            setTimeout(() => {
                onChange && onChange(e);
                setFormValue && setFormValue(value);
            });
        }
    }, [autoHeight, maxLength, onChange, onOverMaxLength, setFormValue]);
    const handleFocus = useCallback((e) => {
        isOnInputExcuted.current = false;
        onFocus && onFocus(e);
    }, [onFocus]);
    const handleBlur = useCallback((e) => {
        if (isIOS()) {
            setTimeout(() => {
                /* 解决iOS微信input失去焦点页面不回弹的问题 */
                if (window.scrollBy) {
                    window.scrollBy(0, 0);
                }
                else if (document.activeElement) {
                    document.activeElement.scrollIntoView(true);
                }
            }, 10);
            /* 解决iOS微信使用原生输入法点击系统提示的内容不触发onChange事件的问题 */
            handleChange(e);
        }
        onBlur && onBlur(e);
    }, [handleChange, onBlur]);
    const handleComposition = useCallback((e) => {
        if (e.type === 'compositionend') {
            isOnComposition.current = false;
            handleChange(e);
        }
        else {
            isOnComposition.current = true;
        }
    }, [handleChange]);
    const handleClear = useCallback(() => {
        onClear && onClear();
        setTextareaValue('');
        setFormValue && setFormValue(undefined);
    }, [onClear, setFormValue]);
    const wrapCls = useMemo(() => {
        return classnames(prefixCls('wrap'), {
            [prefixCls('wrap-clear')]: allowClear
        }, {
            [prefixCls('wrap-pagination')]: pagination
        }, wrapClassName);
    }, [allowClear, pagination, wrapClassName]);
    const textareaCls = useMemo(() => {
        return classnames([prefixCls(), className]);
    }, [className]);
    const textareaSty = useMemo(() => {
        return Object.assign(Object.assign({}, style), { height });
    }, [height, style]);
    const isOverMaxLength = useMemo(() => {
        return maxLength && textareaValue.length >= maxLength;
    }, [maxLength, textareaValue.length]);
    const clearIconCls = useMemo(() => {
        return classnames([prefixCls('clear-icon'), { [prefixCls('clear-icon-show')]: textareaValue.length !== 0 }]);
    }, [textareaValue.length]);
    return (React.createElement("div", { className: wrapCls },
        React.createElement("textarea", Object.assign({ ref: $textarea, id: id.current, className: textareaCls, style: textareaSty, value: textareaValue, rows: rows, autoComplete: autoComplete, onChange: handleChange, onFocus: handleFocus, onBlur: handleBlur, onCompositionStart: handleComposition, onCompositionEnd: handleComposition }, restProps)),
        maxLength && pagination && (React.createElement("div", { className: prefixCls('pagination') },
            React.createElement("span", { className: isOverMaxLength ? prefixCls('pagination-warning') : '' }, isOverMaxLength ? maxLength : textareaValue.length),
            "/",
            maxLength)),
        allowClear && (React.createElement("label", { className: clearIconCls, htmlFor: id.current },
            React.createElement(Icon, { name: "close-circle", onClick: handleClear })))));
};
export default Textarea;
